<template>
  <div class="player-modal-overlay">
    <div class="player-modal">
      <!-- Header -->
      <div class="modal-header">
        <div class="header-content">
          <div class="header-icon">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <circle
                cx="12"
                cy="7"
                r="4"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>
          <h2 class="modal-title">Create Player</h2>
        </div>
        <button class="close-button" @click="handleCancel">
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M15 5L5 15M5 5L15 15"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
      </div>

      <!-- Content -->
      <div class="modal-content" v-if="!isUploadingNewPlayer">
        <!-- User ID Section -->
        <div class="form-section">
          <div class="section-header">
            <div class="section-icon">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M16 7C16 9.20914 14.2091 11 12 11C9.79086 11 8 9.20914 8 7C8 4.79086 9.79086 3 12 3C14.2091 3 16 4.79086 16 7Z"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M12 14C8.13401 14 5 17.134 5 21H19C19 17.134 15.866 14 12 14Z"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </div>
            <div class="section-info">
              <h3>USER IDENTIFICATION</h3>
              <p>Enter the unique user identifier</p>
            </div>
          </div>

          <div class="input-container">
            <div class="input-wrapper">
              <input
                type="text"
                id="userId"
                v-model="formData.userId"
                placeholder="Enter user ID"
                class="form-input"
                :class="{
                  error: !formData.userId && formValidated,
                  success: formData.userId && formValidated,
                }"
                required
              />
              <div class="input-icon">
                <svg
                  v-if="formData.userId"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M20 6L9 17L4 12"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
                <svg
                  v-else-if="!formData.userId && formValidated"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    stroke-width="2"
                  />
                  <line
                    x1="15"
                    y1="9"
                    x2="9"
                    y2="15"
                    stroke="currentColor"
                    stroke-width="2"
                  />
                  <line
                    x1="9"
                    y1="9"
                    x2="15"
                    y2="15"
                    stroke="currentColor"
                    stroke-width="2"
                  />
                </svg>
              </div>
            </div>
            <small
              v-if="!formData.userId && formValidated"
              class="error-message"
            >
              User ID is required
            </small>
          </div>
        </div>

        <!-- Ban Status Section -->
        <div class="form-section">
          <div class="section-header">
            <div class="section-icon">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="2"
                />
                <path
                  d="M4.93 4.93L19.07 19.07"
                  stroke="currentColor"
                  stroke-width="2"
                />
              </svg>
            </div>
            <div class="section-info">
              <h3>BAN STATUS</h3>
              <p>Set whether this player is banned from chat</p>
            </div>
          </div>

          <div class="toggle-container">
            <div class="toggle-wrapper">
              <input
                type="checkbox"
                id="isBannedChat"
                v-model="formData.isBannedChat"
                class="toggle-input"
              />
              <label for="isBannedChat" class="toggle-label">
                <div class="toggle-switch">
                  <div class="toggle-thumb"></div>
                </div>
                <span class="toggle-text">
                  {{ formData.isBannedChat ? "Banned" : "Not Banned" }}
                </span>
              </label>
            </div>
          </div>
        </div>
      </div>

      <!-- Loading State -->
      <div class="loading-state" v-if="isUploadingNewPlayer">
        <div class="loading-spinner"></div>
        <p>Creating player...</p>
      </div>

      <!-- Actions -->
      <div class="modal-actions" v-if="!isUploadingNewPlayer">
        <button class="cancel-btn" @click="handleCancel">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M18 6L6 18M6 6L18 18"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          Cancel
        </button>
        <button
          class="create-btn"
          @click="handleCreate"
          :disabled="!formData.userId"
          :class="{ disabled: !formData.userId }"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <circle
              cx="12"
              cy="7"
              r="4"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          Create Player
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import Swal from "sweetalert2";
import { postFetch } from "@/api/axiosClient";

export default {
  name: "PlayerManager",
  data() {
    return {
      formData: {
        userId: "",
        isBannedChat: false,
      },
      isUploadingNewPlayer: false,
      formValidated: false,
    };
  },
  props: {
    createPlayer: Function,
  },
  computed: {
    ...mapGetters(["isMenuOpen"]),
  },
  methods: {
    resetForm() {
      this.formData = {
        userId: "",
        isBannedChat: false,
      };
      this.formValidated = false;
    },
    async handleCreate() {
      this.formValidated = true;

      if (!this.formData.userId) {
        Swal.fire({
          title: "Error",
          text: "Please type a valid User Id",
          icon: "error",
        });
        return;
      }

      this.isUploadingNewPlayer = true;
      try {
        const response = await postFetch(`/players/create`, {
          user_id: this.formData.userId,
          is_banned_chat: this.formData.isBannedChat,
          skin_id:
            (this.$route.params.skinFilt && this.$route.params.skinFilt !== "all"
              ? parseInt(this.$route.params.skinFilt)
              : (this.$store.getters.skinStorage
                  ? parseInt(this.$store.getters.skinStorage)
                  : this.formData.userId.split("_")[0])),
        });

        if (response?.data) {
          Swal.fire({
            title: "Success!",
            text: "Player added successfully",
          }).then(() => {
            window.location.reload();
          });
        } else {
          Swal.fire({
            title: "Warning",
            text: "Player already exists or no data returned.",
            icon: "warning",
          });
        }
      } catch (error) {
        console.error("Errore durante la creazione del player:", error);
        Swal.fire({
          title: "Error",
          text: error?.response?.data?.error || "Failed to create the Player.",
          icon: "error",
        });
      } finally {
        this.isUploadingNewPlayer = false;
      }
    },

    handleCancel() {
      this.createPlayer(false);
      this.resetForm();
    },
  },
};
</script>

<style scoped>
.player-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9876543210;
  padding: 1rem;
}

.player-modal {
  background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
  border-radius: 24px;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.25);
  border: 1px solid rgba(148, 163, 184, 0.1);
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  overflow: hidden;
  position: relative;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
  border-bottom: 1px solid rgba(148, 163, 184, 0.1);
}

.header-content {
  display: flex;
  align-items: center;
  gap: 16px;
}

.header-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  box-shadow: 0 8px 16px rgba(16, 185, 129, 0.3);
}

.modal-title {
  color: white;
  font-size: 24px;
  font-weight: 600;
  margin: 0;
  letter-spacing: -0.025em;
}

.close-button {
  width: 25px;
  height: 25px;
  border-radius: 10px;
  background: rgba(148, 163, 184, 0.1);
  border: none;
  color: #94a3b8;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.close-button:hover {
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
}

.modal-content {
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.form-section {
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
}

.section-header {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.section-icon {
  width: 20px;
  height: 20px;
  color: #10b981;
  margin-top: 2px;
}

.section-info h3 {
  color: white;
  font-size: 14px;
  font-weight: 600;
  margin: 0 0 4px 0;
  letter-spacing: 0.05em;
}

.section-info p {
  color: #94a3b8;
  font-size: 14px;
  margin: 0;
  line-height: 1.5;
}

.input-container {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.input-wrapper {
  position: relative;
}

.form-input {
  width: 100%;
  padding: 16px 20px;
  padding-right: 48px;
  background: rgba(30, 41, 59, 0.5);
  border: 1px solid rgba(148, 163, 184, 0.2);
  border-radius: 12px;
  color: white;
  font-size: 16px;
  transition: all 0.3s ease;
  outline: none;
}

.form-input::placeholder {
  color: #64748b;
}

.form-input:focus {
  border-color: #10b981;
  background: rgba(16, 185, 129, 0.05);
  box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
}

.form-input.success {
  border-color: #10b981;
  background: rgba(16, 185, 129, 0.05);
}

.form-input.error {
  border-color: #ef4444;
  background: rgba(239, 68, 68, 0.05);
}

.input-icon {
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: #10b981;
}

.form-input.error + .input-icon {
  color: #ef4444;
}

.error-message {
  color: #ef4444;
  font-size: 12px;
  margin-left: 4px;
}

.toggle-container {
  display: flex;
  align-items: center;
}

.toggle-wrapper {
  display: flex;
  align-items: center;
  gap: 16px;
}

.toggle-input {
  display: none;
}

.toggle-label {
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
}

.toggle-switch {
  width: 52px;
  height: 28px;
  background: rgba(148, 163, 184, 0.3);
  border-radius: 14px;
  position: relative;
  transition: all 0.3s ease;
  border: 1px solid rgba(148, 163, 184, 0.2);
}

.toggle-input:checked + .toggle-label .toggle-switch {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  border-color: #10b981;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

.toggle-thumb {
  width: 22px;
  height: 22px;
  background: white;
  border-radius: 50%;
  position: absolute;
  top: 2px;
  left: 2px;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.toggle-input:checked + .toggle-label .toggle-thumb {
  transform: translateX(24px);
}

.toggle-text {
  color: #e2e8f0;
  font-size: 14px;
  font-weight: 500;
  min-width: 80px;
}

.toggle-input:checked + .toggle-label .toggle-text {
  color: white;
}

.loading-state {
  padding: 60px 32px;
  text-align: center;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(16, 185, 129, 0.2);
  border-top: 3px solid #10b981;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.loading-state p {
  color: #94a3b8;
  font-size: 16px;
  margin: 0;
}

.modal-actions {
  display: flex;
  gap: 12px;
  padding: 1rem;
  border-top: 1px solid rgba(148, 163, 184, 0.1);
}

.cancel-btn {
  flex: 1;
  padding: 14px 20px;
  border-radius: 12px;
  border: 1px solid rgba(148, 163, 184, 0.2);
  background: rgba(148, 163, 184, 0.1);
  color: #94a3b8;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.2s ease;
}

.cancel-btn:hover {
  background: rgba(148, 163, 184, 0.2);
  color: white;
}

.create-btn {
  flex: 1;
  padding: 14px 20px;
  border-radius: 12px;
  border: none;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.2s ease;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
  position: relative;
  overflow: hidden;
}

.create-btn:not(.disabled):hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(16, 185, 129, 0.4);
}

.create-btn.disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

@media (max-width: 640px) {
  .player-modal {
    margin: 10px;
    max-width: none;
  }

  .modal-actions {
    flex-direction: column;
  }

  .modal-title {
    font-size: 17px;
  }
}
</style>
<style lang="scss" scoped>
@import "@/assets/styles/views/Freebets.scss";

/* Stile per lo switch */
.switch-group {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 10px;
}

.switch {
  position: relative;
  display: inline-block;
  width: 40px;
  height: 20px;
}

.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  transition: 0.4s;
  border-radius: 20px;
}

.slider:before {
  position: absolute;
  content: "";
  height: 14px;
  width: 14px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  transition: 0.4s;
  border-radius: 50%;
}

input:checked + .slider {
  background-color: #28a745;
}

input:checked + .slider:before {
  transform: translateX(18px);
}

.switch-container {
  display: flex;
  align-items: center;
  gap: 10px;
}

.status-text {
  font-weight: bold;
  font-size: 14px;
}
</style>
